import * as React from 'react';

import {
  Button, Checkbox, Dropdown, Menu, Space,
} from '@revfluence/fresh';
import { AngleDownIcon } from '@revfluence/fresh-icons/solid/esm';

import { Button as ShadCnBtn } from '@frontend/shadcn/components/ui/button';
import { SearchInput, useSearchWithDebounce } from '@frontend/app/components/SearchInput';
import styles from './Filter.scss';

export interface FilterOption<T> {
  label: string;
  value: T;
}

export type FilterProps<T> = {
  defaultLabel?: string;
  allLabel?: string;
  multipleLabel?: string;
  options: FilterOption<T>[];
  selection?: FilterOption<T>[];
  onFilterChange?: (options: FilterOption<T>[]) => void;
  isCheckbox: boolean;
  refreshUi: boolean;
};

interface FilterOptions<T> {
  options: FilterOption<T>[];
  onChange: (option: FilterOption<T> | FilterOption<T>[], checked: boolean) => void;
  isCheckbox: boolean;
  selectedOptions?: FilterOption<T>[];
  pendingOptions?: FilterOption<T>[];
  onApply?: () => void;
}

const { useState, useEffect, useMemo } = React;

function FilterDropdown<T>({
  options,
  onChange,
  isCheckbox,
  selectedOptions,
  pendingOptions,
  onApply,
}: FilterOptions<T>) {
  const {
    searchText,
    inputValue,
    handleSearchChange,
    error,
    checkNoResults,
    isLoading: isSearchLoading,
  } = useSearchWithDebounce({
    searchAnalytics: {
      enabled: true,
      searchContext: 'projects',
      metadata: {
        source: 'impact_dashboard_top_nav',
      },
    },
  });

  const selectedValueSet = useMemo(
    () => new Set((pendingOptions ?? selectedOptions ?? []).map((o) => o.value)),
    [pendingOptions, selectedOptions]
  );

  const filteredOptions = useMemo(() =>
    options.filter((option) =>
      !searchText || option.label.toLowerCase().includes(searchText.toLowerCase())),
    [options, searchText]);

  useEffect(() => {
    checkNoResults(filteredOptions.length > 0);
  }, [filteredOptions.length, checkNoResults]);

  const isAllSelected = useMemo(() => {
    return options.length > 0 && options.every((option) => selectedValueSet.has(option.value));
  }, [options, selectedValueSet]);

  const handleSelectAll = (checked: boolean) => {
    if (!isCheckbox) return;

    // Pass all options instead of filtered ones
    onChange(options, checked);
  };

  return (
    <div onClick={(e) => isCheckbox && e.stopPropagation()} className={styles.dropdownContainer}>
      <Menu className={styles.ProjectDropdown}>
        <div onClick={(e) => e.stopPropagation()}>
          <SearchInput
            value={inputValue}
            onChange={handleSearchChange}
            placeholder="Search projects..."
            isLoading={isSearchLoading}
          />
        </div>
        <div className="border-b" style={{ borderColor: '#dadcde' }} />

        {/* Scrollable content area */}
        <div className={styles.scrollableContent}>
          {error ? (
            <Menu.Item>
              <div className={styles.searchError}>{error}</div>
            </Menu.Item>
          ) : (
            <>
              {isCheckbox && (
                <Menu.Item>
                  <Checkbox
                    className={styles.Checkbox}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  >
                    Select All
                  </Checkbox>
                </Menu.Item>
              )}
              {filteredOptions.map((option) => {
                const { label, value } = option;
                return (
                  <Menu.Item key={String(value)}>
                    {isCheckbox ? (
                      <Checkbox
                        className={styles.Checkbox}
                        checked={selectedValueSet.has(value)}
                        onChange={(e) => {
                          onChange(option, e.target.checked);
                        }}
                      >
                        {label}
                      </Checkbox>
                    ) : (
                      <button
                        type="button"
                        className="w-full text-left px-2 py-1 hover:bg-muted/50"
                        onClick={() => {
                          onChange(option, true);
                        }}
                      >
                        {label}
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </>
          )}
        </div>

        {/* Sticky Apply button - always shown at bottom for checkbox filters */}
        {isCheckbox && (
          <div className={styles.stickyFooter}>
            <div className="border-b" style={{ borderColor: '#dadcde' }} />
            <Menu.Item>
              <div className={styles.actionButtons}>
                <ShadCnBtn
                  size="default"
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onApply?.();
                  }}
                >
                  Apply
                </ShadCnBtn>
              </div>
            </Menu.Item>
          </div>
        )}
      </Menu>
    </div>
  );
}

function Filter<T>({
  options,
  defaultLabel = 'All Data',
  allLabel = 'All Projects',
  multipleLabel = 'Multiple Projects',
  onFilterChange,
  isCheckbox,
  selection: selectionProp = [],
  refreshUi,
}: FilterProps<T>) {
  const [label, setLabel] = useState(defaultLabel);
  const [selection, setSelection] = useState<FilterOption<T>[]>(selectionProp);
  const [pendingSelection, setPendingSelection] = useState<FilterOption<T>[]>(selectionProp);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setSelection(selectionProp);
    setPendingSelection(selectionProp);
  }, [selectionProp]);

  const onPendingSelectionChange = (option: FilterOption<T> | FilterOption<T>[], checked: boolean) => {
    let newPendingSelection: FilterOption<T>[];

    if (checked) {
      if (isCheckbox) {
        if (Array.isArray(option)) {
          newPendingSelection = option;
        } else {
          newPendingSelection = [...pendingSelection, option];
        }
      } else {
        newPendingSelection = [option as FilterOption<T>];
      }
    } else {
      if (Array.isArray(option)) {
        newPendingSelection = [];
      } else {
        newPendingSelection = pendingSelection.filter(
          (opt) => opt.value !== (option as FilterOption<T>).value
        );
      }
    }

    setPendingSelection(newPendingSelection);

    if (!isCheckbox) {
      setSelection(newPendingSelection);
      if (typeof onFilterChange === 'function') {
        onFilterChange(newPendingSelection);
      }
    }
  };

  const handleApply = () => {
    setSelection(pendingSelection);
    if (typeof onFilterChange === 'function') {
      onFilterChange(pendingSelection);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (selection.length === 0) {
      return setLabel(defaultLabel);
    }

    if (selection.length === 1) {
      return setLabel(selection[0].label);
    }

    if (selection.length > 1) {
      return setLabel(multipleLabel);
    }
    if (selection.length === options.length) {
      return setLabel(allLabel);
    }
  }, [selection, defaultLabel, allLabel, multipleLabel, options.length]);

  return (
    <Dropdown
      overlay={(
        <FilterDropdown
          {...{
            options,
            onChange: onPendingSelectionChange,
            isCheckbox,
            selectedOptions: selection,
            pendingOptions: pendingSelection,
            onApply: handleApply,
          }}
        />
      )}
      trigger={['click']}
      visible={isOpen}
      onVisibleChange={(visible) => {
        setIsOpen(visible);
        // When closing dropdown, always revert pending selection to last applied selection
        if (!visible && isCheckbox) {
          setPendingSelection(selection);
        }
      }}
    >
      {
        refreshUi ? (
          <ShadCnBtn
            size="sm"
            variant="outlineHeader"
            className="font-inter"
          >
            {label}
            <AngleDownIcon className="ml-2" />
          </ShadCnBtn>
        ) : (
          <Button>
            <Space>
              {label}
              <AngleDownIcon />
            </Space>
          </Button>
        )
      }
    </Dropdown>
  );
}

export default Filter;
